/**
 * MIKOR VAN VÉGE EGY KÉZI NAV-KÖRNEK (#37) — tiszta függvény, React nélkül.
 *
 * ⚠️ MIÉRT KÜLÖN FÁJL. Ez a döntés a követés EGYETLEN kényes pontja, és
 * mindkét irányban drágán téved: korán kimondva a lista FÉLKÉSZ állapotot
 * mutat késznek (ez a 2.4d „hamis nulla" bukásmódja), későn kimondva a
 * könyvelő egy kész kör fölött vár tovább — vagyis pont az marad meg, amit a
 * #37 meg akart szüntetni. Egy `useEffect` belsejében ez nem mérhető: az
 * időzítőt, a hálózatot és a React életciklusát is meg kellene hozzá
 * hamisítani.
 *
 * ═══ KÉT BEFEJEZÉS, ÉS MINDKETTŐ KELL ═══
 *
 * **A nevező betelt** — `finished + failed + notConnected + upToDate >= queued`.
 * Ez a tiszta eset.
 *
 * **A kör elcsendesedett** — a nevező ugyanis FELSŐ BECSLÉS. A `queued` minden
 * bekötött céget beleszámol, a munkás viszont kihagyja azt, amelyiknek nincs
 * mit lekérdeznie: olyankor `SyncRun` sem születik (`nav-sync.job.ts`: „NINCS
 * MIT KÉRDEZNI — és ilyenkor NEM írunk SyncRun-t"). Az a cég soha nem kerül át
 * a „kész" oldalra, tehát pusztán a nevezőt figyelve a sáv ÖRÖKRE 9/12-n
 * állna, és a lista sosem frissülne magától.
 *
 * ⚠️ A CSEND HÁROM TÉNYBŐL ÁLL, NEM EGYBŐL. Egy lassú cég alatt a `finished`
 * percekig nem mozdul — ha csak azt néznénk, egy DOLGOZÓ körre mondanánk ki,
 * hogy megállt. Ezért számít a `pagesFetched` (életjel lapról lapra) és a
 * `running` is (ami fut, az nem csend).
 *
 * ⚠️ ÉS A CSEND CSAK AZUTÁN SZÁMÍT, HOGY A KÖR MEGMOZDULT. Ez a legdrágább
 * hiba, amit itt el lehet követni, és nem elméleti: EGY sor van az egész
 * rendszerben (`bullmq.job-queue.ts`, `QUEUE_NAME = 'pontco'`,
 * `concurrency: 1`), amiben a kinyerés, a postaláda és a replikáció is áll.
 * Egyetlen OCR-hívás a `nav.sync` feladatok ELŐTT elég ahhoz, hogy a kör
 * percekig el se induljon — és egy induló kör állapota `{0,0,0,0}`, ami
 * MOZDULATLAN. A kezdőállapotot csendnek véve a felület tizenöt másodperc
 * után „Kész"-t mondana, és újratöltené a listát egy körre, ami el sem
 * kezdődött. Pontosan az a hamis kész, ami ellen az egész tétel szól.
 *
 * ⚠️ ENNEK VOLT EGY ÁRA, ÉS AZT A BACKEND VETTE LE. Amíg csak a `sync_runs`
 * látszott, egy VALÓBAN üres kör — ahol egyik cégnél sincs mit lekérdezni,
 * tehát futás sem születik — nem tudott „kész"-ként zárni: kívülről
 * ugyanolyan `{0,0,0,0}`, mint ami el sem indult. Három percig pörgött a sáv,
 * aztán „a szokásosnál tovább tart".
 *
 * A megoldás az `upToDate`: a munkás a naprakész cégnél `SyncCursor.checkedAt`-et
 * ír (`nav-sync.job.ts`), és a státusz ezeket megszámolja. Így a naprakész cég
 * MEGMOZDULÁSNAK számít, és a nevezőbe is beleszámít — az üres kör azonnal
 * zárul, `SyncRun` nélkül is. A `sync_runs` érintetlen marad, tehát az
 * esedékesség (`isCompanyDue`) nem mérgeződik meg.
 * *(Code review fogta meg, 2026-09-15; Viktor döntése ugyanaznap.)*
 */

/** Amit a `GET /nav/sync/status` válaszol — a backend `NavSyncProgress`-e. */
export interface NavSyncProgress {
  finished: number;
  failed: number;
  /**
   * Ennyi cégen azért bukott a lekérdezés, mert NINCS BEKÖTVE a NAV-hoz (#38).
   *
   * ⚠️ A `failed`-BŐL KI VAN VÉVE, ÉS EZ A KÖVETÉST IS ÉRINTI. Bárhol, ahol
   * ez a modul a `failed`-et ELINTÉZETTSÉGKÉNT adja hozzá — a nevező, a
   * „megmozdult-e", a hátralévő —, ott a `notConnected` is oda tartozik: a
   * cégen a kör VÉGET ÉRT, csak nem sikerrel. Kihagyva a sáv soha nem érné el
   * a nevezőt, és egy csupa bekötetlen kör három percig pörögne, aztán „a
   * szokásosnál tovább tart"-ot mondana — pontosan a #37 bukásmódja.
   */
  notConnected: number;
  running: number;
  pagesFetched: number;
  /**
   * Ennyi ÚJ NAV-sor keletkezett a körben.
   *
   * ⚠️ NEM UGYANAZ, MINT A `finished`. Az azt mondja, hány cégen ért véget a
   * lekérdezés; ez azt, hogy lett-e belőle adat. A záró mondat ezen fordul
   * meg (#37).
   */
  createdRows: number;
  /** Ennyi cégnél NEM VOLT MIT KÉRDEZNI — megvolt már a mai adat. */
  upToDate: number;
  medianRunSeconds: number | null;
}

/** A követés emlékezete két lekérdezés között. */
export interface FollowMemory {
  /** Az előző kör érdemi állapota, összefűzve. */
  signature: string;
  /**
   * Hány egymás utáni VÁLTOZATLAN ÁTMENET volt — nem hány kör.
   *
   * ⚠️ AZ ELSŐ LEKÉRDEZÉS AZ ALAPÁLLÁS, és nulla marad: nincs mihez
   * hasonlítani. A küszöb elérése tehát `quietPolls + 1` kört jelent, és ez
   * SZÁNDÉKOS — épp egy lekérdezésnyi többlet-türelem afelett, amennyit a
   * konstans ígér.
   */
  quiet: number;
}

export const EMPTY_MEMORY: FollowMemory = { signature: '', quiet: 0 };

/**
 * `continue` — kérdezz újra. `done` — a kör lefutott, a lista frissíthető.
 * `stalled` — nem várunk tovább, DE a munka megy: a listát NEM frissítjük.
 */
export type FollowVerdict = 'continue' | 'done' | 'stalled';

export interface FollowInput {
  progress: NavSyncProgress;
  /** A nevező: ennyi céget tett sorba a `202`. */
  queued: number;
  previous: FollowMemory;
  /** Mennyi telt el a kör indítása óta. */
  elapsedMs: number;
  /** Ennyi mozdulatlan kör után mondjuk ki a befejezést. */
  quietPolls: number;
  /** Ennyi idő után nem várunk tovább egy MOZGÓ körre. */
  maxWaitMs: number;
  /**
   * Ennyi idő után nem várunk tovább egy meg sem mozdult körre.
   *
   * Rövidebb a `maxWaitMs`-nél: itt nincs mit nézni, tehát a türelemnek nincs
   * hozama. Nem nulla viszont, mert a közös sorban a kör mögé kerülhet egy
   * futó kinyerés — a percekben mért várakozás normális üzem, nem baj.
   */
  coldWaitMs: number;
}

export function followStep(input: FollowInput): { verdict: FollowVerdict; memory: FollowMemory } {
  const { progress, previous } = input;

  const signature = `${progress.finished}:${progress.failed}:${progress.notConnected}:${progress.running}:${progress.pagesFetched}:${progress.createdRows}:${progress.upToDate}`;
  // ⚠️ A `running === 0` FELTÉTEL NÉLKÜL EZ HAMIS ŐR LENNE. Egy egycéges,
  // sokáig tartó lekérdezés alatt a három szám mozdulatlan — a kör mégis megy.
  const quiet = signature === previous.signature && progress.running === 0 ? previous.quiet + 1 : 0;
  const memory = { signature, quiet };

  /**
   * MEGMOZDULT-E A KÖR EGYÁLTALÁN. Bármelyik szám elég hozzá: egy lezárt
   * futás, egy bukott, egy éppen futó, egyetlen lekérdezett lap — vagy egy
   * naprakész cég, amit a munkás megnézett és továbblépett rajta. Amíg mind
   * az öt nulla, a kör még a soron áll: az nem csend, hanem várakozás.
   */
  const started =
    progress.finished +
      progress.failed +
      progress.notConnected +
      progress.running +
      progress.pagesFetched +
      progress.upToDate >
    0;

  /**
   * ⚠️ A NEVEZŐ AZ `upToDate`-ET IS BESZÁMÍTJA, és ettől lett a valóban üres
   * kör újra lezárható (#37). A `queued` CÉGEKET számol — a naprakész cég is
   * elintézett cég, csak nem futott rajta lekérdezés. Enélkül egy kör, ahol
   * mindenkinél megvolt már a mai adat, soha nem érné el a nevezőt, és a
   * csend-ág három perces türelmére szorulna.
   */
  if (
    progress.finished + progress.failed + progress.notConnected + progress.upToDate >=
      input.queued ||
    (started && quiet >= input.quietPolls)
  ) {
    return { verdict: 'done', memory };
  }

  // ⚠️ AZ IDŐKORLÁTOK A LEGUTOLSÓ ÁGON ÁLLNAK. Ha előbb néznénk őket, egy
  // éppen befejeződő kör utolsó lekérdezése `stalled`-ként zárna — és a lista
  // nem frissülne, pedig kész van.
  if (input.elapsedMs >= (started ? input.maxWaitMs : input.coldWaitMs)) {
    return { verdict: 'stalled', memory };
  }

  return { verdict: 'continue', memory };
}

/**
 * HÁNY CÉG VAN HÁTRA, ÉS MENNYI IDŐ.
 *
 * ⚠️ `Math.max(0, …)`: a nevező felső becslés (lásd fent), a számláló viszont
 * valódi futásokból jön — egy negatív „hátralévő" a szövegben `-1 cég`-ként
 * jelenne meg.
 */
export function remainingOf(progress: NavSyncProgress | null, queued: number): number {
  if (progress === null) {
    return queued;
  }
  // Az `upToDate` IS elintézett cég — a sáv különben megállna „3 / 5"-nél egy
  // körben, ahol a maradék kettőnél egyszerűen nem volt mit kérdezni.
  return Math.max(
    0,
    queued - progress.finished - progress.failed - progress.notConnected - progress.upToDate
  );
}

/**
 * A BECSÜLT HÁTRALÉVŐ IDŐ, másodpercben — `null`, ha nincs mire alapozni.
 *
 * A szorzás azért helyes, mert a sor `concurrency: 1`-gyel halad: a cégek nem
 * párhuzamosan futnak, hanem egymás után. Egy párhuzamos sornál ugyanez a
 * képlet a többszörösét mondaná.
 */
export function etaSecondsOf(progress: NavSyncProgress | null, remaining: number): number | null {
  if (progress === null || progress.medianRunSeconds === null) {
    return null;
  }
  return remaining * progress.medianRunSeconds;
}
